import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ConnectionService } from '../connection/connection.service';
import { CommunicationService } from '../services/communication.service';
import { CPMixPanel } from '../services/mixpanel.service';
import { FrontendContentConfigService } from '../services/frontend-content-config.service';
import { UiFlowStateService } from '../services/ui-flow-state.service';

/**
 * Request Connection screen — a 100% migration of web-driver's "Request Connection"
 * side-tray (src/components/Connections/RequestConnection.tsx) into the signup flow.
 *
 * Shown as a full screen AFTER account creation (the synthetic `submit` step) and
 * BEFORE the "All Finished" success page, for EU invited drivers only
 * (gated by isEu && partnerToken in UiFlowStateService.processConfig()).
 *
 * The connection request is a SEPARATE authenticated POST (ConnectionService.requestConnection),
 * NOT part of the save-all account-creation payload — the account must already exist.
 * On failure the user is blocked (inline error + Retry only); on success we advance to
 * the success page via UiFlowStateService.advanceFromRequestConnection().
 */
@Component({
  selector: 'cp-request-connection',
  templateUrl: './request-connection.component.html',
  styleUrls: ['./request-connection.component.scss']
})
export class RequestConnectionComponent implements OnInit {
  companyId: any;
  connectionInfo: any = {};
  connectionForm: FormGroup = new FormGroup({});
  fieldsLoaded = false;
  errorToken = false;
  pressedSubmit = false;
  isSubmitting = false;
  requestFailed = false;
  logoError = false;
  showTermsModal = false;
  termsText = '';

  constructor(
    private connectionService: ConnectionService,
    private communicationSvc: CommunicationService,
    private mixPanel: CPMixPanel,
    private frontEndConfigSvc: FrontendContentConfigService,
    private stateSvc: UiFlowStateService
  ) { }

  ngOnInit() {
    this.frontEndConfigSvc.setBodyTitle('Request Connection');
    // web-driver identifies the connection by companyId (the `connection=<id>` URL param), not a token.
    this.companyId = this.communicationSvc.getConnectionId();
    const urlSearchParams = new URLSearchParams(window.location.search);

    if (!this.companyId) {
      this.errorToken = true;
      this.fieldsLoaded = true;
      return;
    }

    this.connectionService.getConnectionInfo(this.companyId).subscribe((data: any) => {
      if (!data || (!data.connectionFields && !data.connectionName && !data.orgName)) {
        this.errorToken = true;
        this.fieldsLoaded = true;
        return;
      }
      this.connectionInfo = data;
      // Dynamic fields, exactly like web-driver: there may be zero, one, or many.
      (data.connectionFields || []).forEach((field: any) => {
        const prefill = urlSearchParams.get(field.key) || '';
        this.connectionForm.addControl(
          field.key,
          new FormControl(prefill, { validators: [Validators.required], updateOn: 'change' })
        );
      });
      this.fieldsLoaded = true;
    }, () => {
      this.errorToken = true;
      this.fieldsLoaded = true;
    });
  }

  get orgName(): string {
    return this.connectionInfo?.orgName || '';
  }

  get orgInitial(): string {
    return this.orgName ? this.orgName.charAt(0).toUpperCase() : '';
  }

  get logoSrc(): string {
    if (this.connectionInfo?.logoUrl) {
      return this.connectionInfo.logoUrl;
    }
    if (this.connectionInfo?.logo) {
      const ext = this.connectionInfo.logoExtension || this.connectionInfo.logo_extension || 'png';
      return `data:image/${ext};base64,${this.connectionInfo.logo}`;
    }
    return '';
  }

  // web-driver renders a second Terms link when the connection carries company terms.
  get companyTermsAvailability(): boolean {
    return !!this.connectionInfo?.companyTcLink;
  }

  fieldError(key: string): boolean {
    const control = this.connectionForm.get(key);
    return !!control && control.invalid && (control.touched || this.pressedSubmit);
  }

  onLogoError() {
    this.logoError = true;
  }

  submitConnection() {
    this.pressedSubmit = true;
    this.requestFailed = false;
    if (this.connectionForm.invalid) {
      return;
    }
    this.isSubmitting = true;

    // Mirror web-driver: send the connection's fields with the entered values.
    const connectionFields = (this.connectionInfo.connectionFields || [])
      .filter((field: any) => field && field.key)
      .map((field: any) => ({
        ...field,
        value: this.connectionForm.get(field.key)?.value
      }));

    this.mixPanel.connectionContinue();
    this.connectionService.requestConnection({
      companyId: this.connectionInfo.companyId || this.companyId,
      connectionFields
    }).subscribe(
      () => {
        this.isSubmitting = false;
        this.stateSvc.advanceFromRequestConnection();
      },
      () => {
        // Blocked on failure: show inline error + Retry, no way forward until it succeeds.
        this.isSubmitting = false;
        this.requestFailed = true;
      }
    );
  }

  onTermsClick(event: any) {
    if (event?.target?.nodeName !== 'A') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    // First anchor = ChargePoint/service terms, second = company terms (company-terms variant).
    const anchors = event.currentTarget.querySelectorAll('a');
    const idx = Array.prototype.indexOf.call(anchors, event.target);
    const link = idx === 1 ? this.connectionInfo.companyTcLink : this.connectionInfo.serviceTcLink;
    if (!link) {
      return;
    }
    this.showTermsModal = false;
    this.termsText = '';
    this.connectionService.getTermsContent(link).subscribe((content: string) => {
      if (content) {
        this.termsText = content;
        this.showTermsModal = true;
      }
    });
  }
}
